import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSave,
  FiRefreshCw,
  FiDownload,
  FiEdit2,
  FiClock,
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiGrid,
  FiLayers,
  FiPlay,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./tournamentCalculator.css";

const TournamentCalculator = () => {
  // State Management
  const [formData, setFormData] = useState({
    numberOfTeams: 8,
    numberOfGroups: 2,
    numberOfCourts: 2,
    matchDuration: 60,
    teamPrefix: "Men's",
    startTime: "05:00",
    startDate: new Date().toISOString().split("T")[0], // Add start date
    eachGroupSameCourt: false,
    bufferTime: 5,
  });

  const [teams, setTeams] = useState([]);
  const [courts, setCourts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingCourt, setEditingCourt] = useState(null);
  const [sortBy, setSortBy] = useState("time");
  const [filterGroup, setFilterGroup] = useState("all");
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);

  // Toast notification functions
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  // Load saved data on mount
  useEffect(() => {
    const savedData = localStorage.getItem("tournamentCalculatorData");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const loadedFormData = parsed.formData || formData;

        // Ensure startDate is properly set
        if (!loadedFormData.startDate) {
          loadedFormData.startDate = new Date().toISOString().split("T")[0];
        }

        setFormData(loadedFormData);
        setTeams(parsed.teams || []);
        setCourts(parsed.courts || []);
        setSchedule(parsed.schedule || []);
        showToast("Tournament data loaded successfully!", "success");
      } catch (e) {
        console.error("Failed to load saved data:", e);
        showToast("Failed to load saved data", "error");
      }
    }
  }, []);

  // Generate teams when form changes
  useEffect(() => {
    if (formData.numberOfTeams > 0 && formData.numberOfGroups > 0) {
      // console.log("Generating teams due to form change:", formData);
      generateTeams();
    }
  }, [formData.numberOfTeams, formData.numberOfGroups, formData.teamPrefix]);

  // Generate courts when form changes
  useEffect(() => {
    if (formData.numberOfCourts > 0) {
      generateCourts();
    }
  }, [formData.numberOfCourts]);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Teams validation
    if (!formData.numberOfTeams || formData.numberOfTeams < 2) {
      newErrors.numberOfTeams = "Minimum 2 teams required";
    } else if (formData.numberOfTeams > 50) {
      newErrors.numberOfTeams = "Maximum 50 teams allowed";
    }

    // Groups validation
    if (!formData.numberOfGroups || formData.numberOfGroups < 1) {
      newErrors.numberOfGroups = "Minimum 1 group required";
    } else if (formData.numberOfGroups > 20) {
      newErrors.numberOfGroups = "Maximum 20 groups allowed";
    }

    // Courts validation
    if (!formData.numberOfCourts || formData.numberOfCourts < 1) {
      newErrors.numberOfCourts = "Minimum 1 court required";
    } else if (formData.numberOfCourts > 20) {
      newErrors.numberOfCourts = "Maximum 20 courts allowed";
    }

    // Match duration validation
    if (!formData.matchDuration || formData.matchDuration < 5) {
      newErrors.matchDuration = "Minimum 5 minutes required";
    } else if (formData.matchDuration > 180) {
      newErrors.matchDuration = "Maximum 180 minutes allowed";
    }

    // Buffer time validation
    if (formData.bufferTime < 0) {
      newErrors.bufferTime = "Buffer time cannot be negative";
    } else if (formData.bufferTime > 60) {
      newErrors.bufferTime = "Maximum 60 minutes buffer time allowed";
    }

    // Team prefix validation
    if (formData.teamPrefix.length > 20) {
      newErrors.teamPrefix = "Team prefix too long (max 20 characters)";
    } else if (formData.teamPrefix.trim().length === 0) {
      newErrors.teamPrefix = "Team prefix cannot be empty";
    }

    // Start time validation
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.startTime)) {
      newErrors.startTime = "Invalid time format (use HH:MM)";
    }

    // Cross-field validation
    if (formData.numberOfTeams < formData.numberOfGroups) {
      newErrors.numberOfGroups = "Cannot have more groups than teams";
    }

    // Check if teams can be evenly distributed
    if (formData.numberOfTeams > 0 && formData.numberOfGroups > 0) {
      const teamsPerGroup = Math.floor(
        formData.numberOfTeams / formData.numberOfGroups
      );
      const remainder = formData.numberOfTeams % formData.numberOfGroups;
      const maxTeamsInGroup = teamsPerGroup + (remainder > 0 ? 1 : 0);

      if (maxTeamsInGroup > 20) {
        newErrors.numberOfGroups =
          "Too many groups - some groups would have more than 20 teams";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate teams
  const generateTeams = () => {
    if (!formData.numberOfTeams || !formData.numberOfGroups) {
      // console.log("Cannot generate teams - missing data:", formData);
      return;
    }

    const teamsPerGroup = Math.floor(
      formData.numberOfTeams / formData.numberOfGroups
    );
    const remainder = formData.numberOfTeams % formData.numberOfGroups;

    const newTeams = [];
    let teamIndex = 1;

    for (let group = 0; group < formData.numberOfGroups; group++) {
      const teamsInThisGroup = teamsPerGroup + (group < remainder ? 1 : 0);

      for (let i = 0; i < teamsInThisGroup; i++) {
        newTeams.push({
          id: `team-${teamIndex}`,
          name: `${formData.teamPrefix} Team ${teamIndex}`,
          group: group + 1,
          originalIndex: teamIndex - 1,
        });
        teamIndex++;
      }
    }

    // console.log("Generated teams:", newTeams);
    setTeams(newTeams);
  };

  // Generate courts
  const generateCourts = () => {
    const newCourts = [];
    for (let i = 1; i <= formData.numberOfCourts; i++) {
      const existingCourt = courts.find((c) => c.id === `court-${i}`);
      newCourts.push({
        id: `court-${i}`,
        name: existingCourt?.name || `Court ${i}`,
        number: i,
      });
    }
    setCourts(newCourts);
  };

  // Round Robin algorithm for each group
  const generateRoundRobinMatches = (groupTeams) => {
    const matches = [];
    const n = groupTeams.length;

    if (n < 2) return matches;

    // Round-robin algorithm
    for (let i = 0; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        matches.push({
          team1: groupTeams[i],
          team2: groupTeams[j],
          group: groupTeams[i].group,
        });
      }
    }

    return matches;
  };

  // Validate schedule for team conflicts
  const validateScheduleForConflicts = (matches) => {
    const conflicts = [];
    const teamSchedules = {};

    // Build team schedules
    matches.forEach((match) => {
      if (!teamSchedules[match.team1]) {
        teamSchedules[match.team1] = [];
      }
      if (!teamSchedules[match.team2]) {
        teamSchedules[match.team2] = [];
      }

      teamSchedules[match.team1].push({
        startTime: match.startTime,
        endTime: match.endTime,
        court: match.court,
        opponent: match.team2,
        matchId: match.id,
      });

      teamSchedules[match.team2].push({
        startTime: match.startTime,
        endTime: match.endTime,
        court: match.court,
        opponent: match.team1,
        matchId: match.id,
      });
    });

    // Check for overlapping matches for each team
    Object.keys(teamSchedules).forEach((teamName) => {
      const teamMatches = teamSchedules[teamName];

      for (let i = 0; i < teamMatches.length; i++) {
        for (let j = i + 1; j < teamMatches.length; j++) {
          const match1 = teamMatches[i];
          const match2 = teamMatches[j];

          // Check if matches overlap
          if (
            (match1.startTime < match2.endTime &&
              match1.endTime > match2.startTime) ||
            (match2.startTime < match1.endTime &&
              match2.endTime > match1.startTime)
          ) {
            conflicts.push({
              team: teamName,
              conflictType: "overlapping_matches",
              match1: {
                id: match1.matchId,
                court: match1.court,
                opponent: match1.opponent,
                startTime: match1.startTime,
                endTime: match1.endTime,
              },
              match2: {
                id: match2.matchId,
                court: match2.court,
                opponent: match2.opponent,
                startTime: match2.startTime,
                endTime: match2.endTime,
              },
            });
          }
        }
      }
    });

    return conflicts;
  };

  // Generate Schedule
  const generateSchedule = async () => {
    if (!validateForm()) {
      showToast(
        "Please fix validation errors before generating schedule",
        "error"
      );
      return;
    }

    setIsGenerating(true);

    try {
      // Simulate processing time for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Ensure teams are generated before creating schedule
      if (teams.length === 0) {
        generateTeams();
        // Wait a bit for teams to be generated
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const allMatches = [];

      // Generate matches for each group
      for (let group = 1; group <= formData.numberOfGroups; group++) {
        const groupTeams = teams.filter((t) => t.group === group);
        const groupMatches = generateRoundRobinMatches(groupTeams);
        allMatches.push(...groupMatches);
      }

      // Assign courts and times
      let currentTime = parseTime(formData.startTime);
      let courtIndex = 0;
      const courtAssignments = {};
      const teamAssignments = {}; // Track when teams are playing

      // Initialize court assignments
      courts.forEach((court) => {
        courtAssignments[court.id] = parseTime(formData.startTime);
      });

      // Initialize team assignments
      teams.forEach((team) => {
        teamAssignments[team.name] = parseTime(formData.startTime);
      });

      const scheduledMatches = [];

      // Process matches one by one to handle conflicts
      for (let index = 0; index < allMatches.length; index++) {
        const match = allMatches[index];
        let assignedCourt;
        let assignedTime;

        // Find the earliest available time for both teams
        const team1NextTime = teamAssignments[match.team1.name];
        const team2NextTime = teamAssignments[match.team2.name];
        const earliestTeamTime = Math.max(team1NextTime, team2NextTime);

        if (formData.eachGroupSameCourt) {
          // Assign each group to specific courts
          const groupCourtIndex = (match.group - 1) % courts.length;
          assignedCourt = courts[groupCourtIndex];

          // Get the next available time for this court
          const courtNextTime = courtAssignments[assignedCourt.id];
          assignedTime = Math.max(courtNextTime, earliestTeamTime);
        } else {
          // Find the court that's free earliest, considering team conflicts
          let bestCourt = null;
          let bestTime = Infinity;

          courts.forEach((court) => {
            const courtNextTime = courtAssignments[court.id];
            const requiredTime = Math.max(courtNextTime, earliestTeamTime);

            if (requiredTime < bestTime) {
              bestTime = requiredTime;
              bestCourt = court;
            }
          });

          assignedCourt = bestCourt;
          assignedTime = bestTime;
        }

        const startTime = assignedTime;
        const endTime = addMinutes(assignedTime, formData.matchDuration);
        const nextMatchTime = addMinutes(endTime, formData.bufferTime);

        // Update court availability
        courtAssignments[assignedCourt.id] = nextMatchTime;

        // Update team availability
        teamAssignments[match.team1.name] = nextMatchTime;
        teamAssignments[match.team2.name] = nextMatchTime;

        const startDate = getDateFromTime(startTime);
        const endDate = getDateFromTime(endTime);

        scheduledMatches.push({
          id: `match-${index + 1}`,
          matchNumber: index + 1,
          team1: match.team1.name,
          team2: match.team2.name,
          court: assignedCourt.name,
          courtNumber: assignedCourt.number,
          group: match.group,
          startTime: startTime,
          endTime: endTime,
          startTimeFormatted: formatTime(startTime, startDate),
          endTimeFormatted: formatTime(endTime, endDate),
          date: startDate,
          duration: formData.matchDuration,
        });
      }

      // Validate the schedule for any remaining conflicts
      const conflicts = validateScheduleForConflicts(scheduledMatches);
      setScheduleConflicts(conflicts);

      if (conflicts.length > 0) {
        console.warn("Schedule conflicts detected:", conflicts);
        showToast(
          `Schedule generated with ${conflicts.length} potential conflicts. Please review the schedule.`,
          "error"
        );
      } else {
        showToast(
          `Schedule generated successfully! ${scheduledMatches.length} matches created with no team conflicts.`,
          "success"
        );
      }

      console.log("Generated schedule:", scheduledMatches);
      setSchedule(scheduledMatches);
    } catch (error) {
      console.error("Error generating schedule:", error);
      showToast("Failed to generate schedule. Please try again.", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  // Time utility functions
  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const addMinutes = (timeInMinutes, minutesToAdd) => {
    return timeInMinutes + minutesToAdd;
  };

  const getDateFromTime = (timeInMinutes) => {
    const days = Math.floor(timeInMinutes / (24 * 60));
    return days;
  };

  const formatTime = (timeInMinutes, dayOffset = 0) => {
    const totalMinutes = timeInMinutes % (24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;

    let timeStr = `${String(displayHours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")} ${period}`;

    if (dayOffset > 0) {
      // Calculate actual date based on start date
      try {
        // Parse start date properly - handle different formats
        let startDate;
        if (formData.startDate) {
          // If it's already a date string, use it directly
          startDate = new Date(formData.startDate);
        } else {
          // Fallback to today's date
          startDate = new Date();
        }

        // Check if date is valid
        if (isNaN(startDate.getTime())) {
          console.error("Invalid start date:", formData.startDate);
          // Fallback to today's date
          startDate = new Date();
        }

        const matchDate = new Date(startDate);
        matchDate.setDate(startDate.getDate() + dayOffset);

        const dateStr = matchDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        timeStr = `${dateStr}, ${timeStr}`;
      } catch (error) {
        console.error("Error formatting date:", error);
        // Fallback: just show time without date
        return timeStr;
      }
    }

    return timeStr;
  };

  // Format time without date for optimization
  const formatTimeOnly = (timeInMinutes) => {
    const totalMinutes = timeInMinutes % (24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;

    return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )} ${period}`;
  };

  // Format date only
  const formatDateOnly = (dayOffset = 0) => {
    if (dayOffset === 0) return null;

    try {
      let startDate;
      if (formData.startDate) {
        startDate = new Date(formData.startDate);
      } else {
        startDate = new Date();
      }

      if (isNaN(startDate.getTime())) {
        startDate = new Date();
      }

      const matchDate = new Date(startDate);
      matchDate.setDate(startDate.getDate() + dayOffset);

      return matchDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Input handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Team editing
  const handleTeamNameChange = (teamId, newName) => {
    // First, get the old team name before updating
    const oldTeam = teams.find((t) => t.id === teamId);
    const oldName = oldTeam ? oldTeam.name : "";

    console.log(`Updating team ${teamId}: "${oldName}" -> "${newName}"`);

    setTeams((prev) => {
      const updated = prev.map((team) =>
        team.id === teamId ? { ...team, name: newName } : team
      );

      // Auto-update schedule if it exists
      if (schedule.length > 0) {
        setSchedule((prevSchedule) =>
          prevSchedule.map((match) => {
            // Update team names in schedule using old name for comparison
            const updatedMatch = {
              ...match,
              team1: match.team1 === oldName ? newName : match.team1,
              team2: match.team2 === oldName ? newName : match.team2,
            };

            // Log if any changes were made
            if (
              updatedMatch.team1 !== match.team1 ||
              updatedMatch.team2 !== match.team2
            ) {
              console.log(
                `Updated match ${match.id}: team1="${updatedMatch.team1}", team2="${updatedMatch.team2}"`
              );
            }

            return updatedMatch;
          })
        );
      }

      return updated;
    });
    setEditingTeam(null);
  };

  // Court editing
  const handleCourtNameChange = (courtId, newName) => {
    setCourts((prev) => {
      const updated = prev.map((court) =>
        court.id === courtId ? { ...court, name: newName } : court
      );

      // Auto-update schedule if it exists
      if (schedule.length > 0) {
        setSchedule((prevSchedule) =>
          prevSchedule.map((match) => {
            const court = updated.find((c) => c.id === courtId);
            if (court && match.courtNumber === court.number) {
              return { ...match, court: newName };
            }
            return match;
          })
        );
      }

      return updated;
    });
    setEditingCourt(null);
  };

  // Save to localStorage
  const handleSave = () => {
    try {
      const dataToSave = {
        formData,
        teams,
        courts,
        schedule,
        savedAt: new Date().toISOString(),
      };

      localStorage.setItem(
        "tournamentCalculatorData",
        JSON.stringify(dataToSave)
      );

      showToast("Tournament data saved successfully!", "success");
    } catch (error) {
      console.error("Save error:", error);
      showToast("Failed to save data. Please try again.", "error");
    }
  };

  // Reset everything
  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all data?")) {
      try {
        localStorage.removeItem("tournamentCalculatorData");
        setFormData({
          numberOfTeams: 8,
          numberOfGroups: 2,
          numberOfCourts: 2,
          matchDuration: 60,
          teamPrefix: "Men's",
          startTime: "05:00",
          startDate: new Date().toISOString().split("T")[0],
          eachGroupSameCourt: false,
          bufferTime: 5,
        });
        setTeams([]);
        setCourts([]);
        setSchedule([]);
        setScheduleConflicts([]);
        setErrors({});
        showToast("Tournament data reset successfully!", "success");
      } catch (error) {
        console.error("Reset error:", error);
        showToast("Failed to reset data. Please try again.", "error");
      }
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (schedule.length === 0) {
      showToast("Please generate schedule first", "error");
      return;
    }

    console.log("Exporting to Excel...");
    console.log("Schedule data:", schedule);

    const workbook = XLSX.utils.book_new();

    // Tournament Info Sheet
    const infoData = [
      ["Tournament Schedule"],
      [""],
      ["Tournament Details:"],
      ["Number of Teams:", formData.numberOfTeams],
      ["Number of Groups:", formData.numberOfGroups],
      ["Number of Courts:", formData.numberOfCourts],
      ["Match Duration:", `${formData.matchDuration} minutes`],
      ["Buffer Time:", `${formData.bufferTime} minutes`],
      ["Start Date:", formData.startDate || "Not specified"],
      ["Start Time:", formData.startTime],
      ["Team Prefix:", formData.teamPrefix],
      [""],
      ["Schedule Statistics:"],
      ["Total Matches:", schedule.length],
      [
        "Matches per Group:",
        Math.ceil(schedule.length / formData.numberOfGroups),
      ],
      [""],
      ["Generated on:", new Date().toLocaleString()],
    ];

    const infoSheet = XLSX.utils.aoa_to_sheet(infoData);
    XLSX.utils.book_append_sheet(workbook, infoSheet, "Info");

    // Schedule Sheet - Enhanced with better data handling
    const scheduleData = [];

    // Add header row
    scheduleData.push([
      "Match #",
      "Group",
      "Team 1",
      "vs",
      "Team 2",
      "Court",
      "Date",
      "Start Time",
      "End Time",
      "Duration (min)",
      "Full Schedule",
    ]);

    // Add data rows
    schedule.forEach((match, index) => {
      console.log(`Match ${index + 1}:`, match);

      // Extract date and time separately for better formatting
      const startTimeOnly = formatTimeOnly(match.startTime);
      const endTimeOnly = formatTimeOnly(match.endTime);
      const dayOffset = getDateFromTime(match.startTime);
      const matchDate = formatDateOnly(dayOffset);

      scheduleData.push([
        match.matchNumber || index + 1,
        `Group ${match.group || 1}`,
        match.team1 || "Unknown",
        "vs",
        match.team2 || "Unknown",
        match.court || "Unknown",
        matchDate || "Day 1",
        startTimeOnly,
        endTimeOnly,
        match.duration || formData.matchDuration,
        `${matchDate || "Day 1"} - ${startTimeOnly} to ${endTimeOnly}`,
      ]);
    });

    console.log("Processed schedule data:", scheduleData);

    const scheduleSheet = XLSX.utils.aoa_to_sheet(scheduleData);

    // Style the header row
    const headerRange = XLSX.utils.decode_range(scheduleSheet["!ref"]);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!scheduleSheet[cellAddress]) continue;
      scheduleSheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2E55B9" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Set column widths for better formatting
    scheduleSheet["!cols"] = [
      { wch: 8 }, // Match #
      { wch: 10 }, // Group
      { wch: 20 }, // Team 1
      { wch: 5 }, // vs
      { wch: 20 }, // Team 2
      { wch: 15 }, // Court
      { wch: 15 }, // Date
      { wch: 12 }, // Start Time
      { wch: 12 }, // End Time
      { wch: 12 }, // Duration
      { wch: 30 }, // Full Schedule
    ];

    XLSX.utils.book_append_sheet(workbook, scheduleSheet, "Schedule");

    // Group-wise Schedule Sheet
    const groupWiseData = [];

    // Add headers
    groupWiseData.push([
      "Group",
      "Match #",
      "Team 1",
      "vs",
      "Team 2",
      "Court",
      "Date",
      "Start Time",
      "End Time",
      "Duration (min)",
    ]);

    // Group matches by group and add them
    Array.from({ length: formData.numberOfGroups }, (_, i) => i + 1).forEach(
      (group) => {
        const groupMatches = schedule.filter((match) => match.group === group);

        if (groupMatches.length > 0) {
          // Add group header
          groupWiseData.push([
            `Group ${group}`,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ]);

          // Add matches for this group
          groupMatches.forEach((match) => {
            const dayOffset = getDateFromTime(match.startTime);
            const matchDate = formatDateOnly(dayOffset);
            const startTimeOnly = formatTimeOnly(match.startTime);
            const endTimeOnly = formatTimeOnly(match.endTime);

            groupWiseData.push([
              "", // Empty group column for sub-rows
              match.matchNumber || "",
              match.team1 || "Unknown",
              "vs",
              match.team2 || "Unknown",
              match.court || "Unknown",
              matchDate || "Day 1",
              startTimeOnly,
              endTimeOnly,
              match.duration || formData.matchDuration,
            ]);
          });

          // Add empty row between groups
          groupWiseData.push(["", "", "", "", "", "", "", "", "", ""]);
        }
      }
    );

    const groupWiseSheet = XLSX.utils.aoa_to_sheet(groupWiseData);

    // Set column widths for group-wise sheet
    groupWiseSheet["!cols"] = [
      { wch: 10 }, // Group
      { wch: 8 }, // Match #
      { wch: 15 }, // Team 1
      { wch: 5 }, // vs
      { wch: 15 }, // Team 2
      { wch: 12 }, // Court
      { wch: 15 }, // Date
      { wch: 12 }, // Start Time
      { wch: 12 }, // End Time
      { wch: 12 }, // Duration
    ];

    XLSX.utils.book_append_sheet(workbook, groupWiseSheet, "Group Schedule");

    // Teams Sheet
    const teamsData = teams.map((team) => ({
      "Team Name": team.name,
      Group: `Group ${team.group}`,
    }));

    const teamsSheet = XLSX.utils.json_to_sheet(teamsData);
    XLSX.utils.book_append_sheet(workbook, teamsSheet, "Teams");

    // Courts Sheet
    const courtsData = courts.map((court) => ({
      "Court Number": court.number,
      "Court Name": court.name,
    }));

    const courtsSheet = XLSX.utils.json_to_sheet(courtsData);
    XLSX.utils.book_append_sheet(workbook, courtsSheet, "Courts");

    // Generate Excel file
    try {
      // Set the default active sheet to Schedule (index 1)
      workbook.SheetNames = [
        "Schedule",
        "Group Schedule",
        "Info",
        "Teams",
        "Courts",
      ];
      workbook.Sheets["Schedule"] = scheduleSheet;
      workbook.Sheets["Group Schedule"] = groupWiseSheet;
      workbook.Sheets["Info"] = infoSheet;
      workbook.Sheets["Teams"] = teamsSheet;
      workbook.Sheets["Courts"] = courtsSheet;

      XLSX.writeFile(
        workbook,
        `Tournament_Schedule_${new Date().getTime()}.xlsx`
      );
      console.log("Excel file generated successfully!");
      showToast(
        "Excel file exported successfully with complete schedule data!",
        "success"
      );
    } catch (error) {
      console.error("Error generating Excel file:", error);
      showToast("Error generating Excel file. Please try again.", "error");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    if (schedule.length === 0) {
      showToast("Please generate schedule first", "error");
      return;
    }

    try {
      const doc = new jsPDF();

      // Title
      doc.setFontSize(20);
      doc.setTextColor(12, 27, 58); // Primary color
      doc.text("Tournament Schedule", 14, 20);

      // Tournament Info
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Teams: ${formData.numberOfTeams} | Groups: ${formData.numberOfGroups} | Courts: ${formData.numberOfCourts}`,
        14,
        28
      );
      doc.text(
        `Match Duration: ${formData.matchDuration} min | Buffer: ${formData.bufferTime} min | Start: ${formData.startTime}`,
        14,
        34
      );
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 40);

      // Schedule by Groups
      let yPosition = 50;

      for (let group = 1; group <= formData.numberOfGroups; group++) {
        const groupMatches = schedule.filter((m) => m.group === group);

        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(46, 85, 185); // Accent color
        doc.text(
          `Group ${group} (${groupMatches.length} matches)`,
          14,
          yPosition
        );
        yPosition += 8;

        const tableData = groupMatches.map((match) => [
          match.matchNumber,
          match.team1,
          "vs",
          match.team2,
          match.court,
          match.startTimeFormatted,
          match.endTimeFormatted,
        ]);

        doc.autoTable({
          startY: yPosition,
          head: [["#", "Team 1", "", "Team 2", "Court", "Start", "End"]],
          body: tableData,
          theme: "striped",
          headStyles: {
            fillColor: [12, 27, 58],
            textColor: [255, 255, 255],
            fontSize: 9,
          },
          styles: {
            fontSize: 8,
            cellPadding: 3,
          },
          columnStyles: {
            0: { cellWidth: 10 },
            2: { cellWidth: 10, halign: "center" },
          },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Save PDF
      doc.save(`Tournament_Schedule_${new Date().getTime()}.pdf`);
      showToast("PDF exported successfully!", "success");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showToast("Error generating PDF. Please try again.", "error");
    }
  };

  // Sort schedule
  const getSortedSchedule = () => {
    let sorted = [...schedule];

    if (filterGroup !== "all") {
      sorted = sorted.filter((m) => m.group === parseInt(filterGroup));
    }

    switch (sortBy) {
      case "time":
        sorted.sort((a, b) => a.startTime - b.startTime);
        break;
      case "court":
        sorted.sort(
          (a, b) => a.courtNumber - b.courtNumber || a.startTime - b.startTime
        );
        break;
      case "group":
        sorted.sort((a, b) => a.group - b.group || a.startTime - b.startTime);
        break;
      default:
        break;
    }

    return sorted;
  };

  // Group colors
  const getGroupColor = (group) => {
    const colors = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-green-500 to-green-600",
      "from-orange-500 to-orange-600",
      "from-pink-500 to-pink-600",
      "from-indigo-500 to-indigo-600",
      "from-red-500 to-red-600",
      "from-teal-500 to-teal-600",
    ];
    return colors[(group - 1) % colors.length];
  };

  const getGroupBgColor = (group) => {
    const colors = [
      "bg-blue-50 border-blue-200",
      "bg-purple-50 border-purple-200",
      "bg-green-50 border-green-200",
      "bg-orange-50 border-orange-200",
      "bg-pink-50 border-pink-200",
      "bg-indigo-50 border-indigo-200",
      "bg-red-50 border-red-200",
      "bg-teal-50 border-teal-200",
    ];
    return colors[(group - 1) % colors.length];
  };

  return (
    <div className="tournament-calculator min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 px-4">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-medium transition-all duration-300 ${
            toast.type === "success"
              ? "bg-green-500"
              : toast.type === "error"
              ? "bg-red-500"
              : "bg-blue-500"
          }`}
        >
          {toast.message}
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#0C1B3A] to-[#2E55B9] bg-clip-text text-transparent">
            Tournament Calculator
          </h1>
        </motion.header>

        {/* Main Content */}
        <main className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Column - Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1 space-y-4"
          >
            {/* Input Form Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] rounded-lg flex items-center justify-center mr-3">
                  <FiGrid className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-[#0C1B3A]">
                  Configuration
                </h2>
              </div>

              <div className="space-y-4">
                {/* Row 1: Teams and Groups */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Teams *
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="50"
                      value={formData.numberOfTeams}
                      onChange={(e) =>
                        handleInputChange(
                          "numberOfTeams",
                          parseInt(e.target.value) || 0
                        )
                      }
                      aria-label="Number of teams"
                      aria-describedby={
                        errors.numberOfTeams ? "teams-error" : undefined
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.numberOfTeams
                          ? "border-red-500"
                          : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.numberOfTeams && (
                      <p
                        id="teams-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.numberOfTeams}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Groups *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.numberOfGroups}
                      onChange={(e) =>
                        handleInputChange(
                          "numberOfGroups",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.numberOfGroups
                          ? "border-red-500"
                          : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.numberOfGroups && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.numberOfGroups}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 2: Courts and Match Duration */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Courts *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.numberOfCourts}
                      onChange={(e) =>
                        handleInputChange(
                          "numberOfCourts",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.numberOfCourts
                          ? "border-red-500"
                          : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.numberOfCourts && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.numberOfCourts}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Match Duration *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.matchDuration}
                      onChange={(e) =>
                        handleInputChange(
                          "matchDuration",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.matchDuration
                          ? "border-red-500"
                          : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.matchDuration && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.matchDuration}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 3: Buffer Time and Team Prefix */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Buffer Time (min)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={formData.bufferTime}
                      onChange={(e) =>
                        handleInputChange(
                          "bufferTime",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.bufferTime ? "border-red-500" : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.bufferTime && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.bufferTime}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Team Prefix
                    </label>
                    <input
                      type="text"
                      maxLength="20"
                      value={formData.teamPrefix}
                      onChange={(e) =>
                        handleInputChange("teamPrefix", e.target.value)
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.teamPrefix ? "border-red-500" : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                      placeholder="e.g., Men's, Women's"
                    />
                    {errors.teamPrefix && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.teamPrefix}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 4: Start Date and Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        handleInputChange("startDate", e.target.value)
                      }
                      className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-[#2E55B9] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) =>
                        handleInputChange("startTime", e.target.value)
                      }
                      className={`w-full px-3 py-2 rounded-lg border-2 ${
                        errors.startTime ? "border-red-500" : "border-gray-200"
                      } focus:border-[#2E55B9] focus:outline-none transition-colors`}
                    />
                    {errors.startTime && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.startTime}
                      </p>
                    )}
                  </div>
                </div>

                {/* Each Group Same Court Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      Each Group on Same Court
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Keep group matches on same court
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleInputChange(
                        "eachGroupSameCourt",
                        !formData.eachGroupSameCourt
                      )
                    }
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      formData.eachGroupSameCourt
                        ? "bg-[#2E55B9]"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        formData.eachGroupSameCourt
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateSchedule}
                  disabled={isGenerating}
                  aria-label={
                    isGenerating
                      ? "Generating schedule, please wait"
                      : "Generate tournament schedule"
                  }
                  className={`w-full py-4 bg-gradient-to-r from-[#0C1B3A] to-[#2E55B9] text-white font-bold rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center space-x-2 ${
                    isGenerating ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <FiPlay className="text-xl" />
                      <span>Generate Schedule</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-[#0C1B3A] mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleSave}
                  aria-label="Save tournament data to browser storage"
                  className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <FiSave />
                  <span>Save to Browser</span>
                </button>

                {/* Excel Export Button - Hidden */}
                {/* <button
                  onClick={exportToExcel}
                  disabled={schedule.length === 0}
                  aria-label={
                    schedule.length === 0
                      ? "Export to Excel (disabled - generate schedule first)"
                      : "Export tournament schedule to Excel"
                  }
                  className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <FiDownload />
                  <span>Export to Excel</span>
                </button> */}

                <button
                  onClick={exportToPDF}
                  disabled={schedule.length === 0}
                  aria-label={
                    schedule.length === 0
                      ? "Export to PDF (disabled - generate schedule first)"
                      : "Export tournament schedule to PDF"
                  }
                  className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <FiDownload />
                  <span>Export to PDF</span>
                </button>

                <button
                  onClick={handleReset}
                  aria-label="Reset all tournament data and start over"
                  className="w-full py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <FiRefreshCw />
                  <span>Reset All</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Middle Column - Schedule Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            {/* Schedule Section */}
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FiCalendar className="text-xl text-[#2E55B9] mr-2" />
                  <h3 className="text-lg font-bold text-[#0C1B3A]">
                    Tournament Schedule
                  </h3>
                </div>

                {schedule.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <select
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                      className="px-2 py-1 rounded border border-gray-200 focus:border-[#2E55B9] focus:outline-none text-xs"
                    >
                      <option value="all">All Groups</option>
                      {Array.from(
                        { length: formData.numberOfGroups },
                        (_, i) => i + 1
                      ).map((group) => (
                        <option key={group} value={group}>
                          Group {group}
                        </option>
                      ))}
                    </select>

                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-2 py-1 rounded border border-gray-200 focus:border-[#2E55B9] focus:outline-none text-xs"
                    >
                      <option value="time">Time</option>
                      <option value="court">Court</option>
                      <option value="group">Group</option>
                    </select>
                  </div>
                )}
              </div>

              {schedule.length > 0 ? (
                <div className="space-y-6">
                  {Array.from(
                    { length: formData.numberOfGroups },
                    (_, i) => i + 1
                  ).map((group) => {
                    // Ensure teams are available
                    const allTeams = teams.length > 0 ? teams : [];
                    const groupTeams = allTeams.filter(
                      (t) => t.group === group
                    );
                    const groupMatches = getSortedSchedule().filter(
                      (m) => m.group === group
                    );

                    // Debug: Log group teams (commented out since teams section is hidden)
                    // console.log(`Group ${group} teams:`, groupTeams);
                    // console.log(`All teams:`, allTeams);
                    // console.log(`Teams state:`, teams);

                    if (
                      filterGroup !== "all" &&
                      parseInt(filterGroup) !== group
                    ) {
                      return null;
                    }

                    return (
                      <div key={group} className="space-y-3">
                        <div
                          className={`inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r ${getGroupColor(
                            group
                          )} text-white font-bold text-sm`}
                        >
                          <FiLayers className="mr-1.5 text-xs" />
                          Group {group} ({groupMatches.length} matches)
                        </div>

                        {/* Teams in Group - Hidden as per user request */}
                        {/* <div className="p-4 rounded-lg border border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <div className="mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                <FiUsers className="mr-2 text-[#2E55B9]" />
                                Teams in Group {group} ({groupTeams.length} teams)
                              </h4>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {groupTeams.length > 0 ? (
                                groupTeams.map((team, index) => (
                              <div
                                key={team.id}
                                    className="flex items-center px-3 py-2 rounded-lg bg-white border border-gray-200 text-xs shadow-sm hover:shadow-md transition-all duration-200 hover:border-[#2E55B9]"
                              >
                                    <div className="w-6 h-6 rounded-full bg-[#2E55B9] text-white flex items-center justify-center text-xs font-bold mr-2">
                                      {index + 1}
                                    </div>
                                    <span className="font-medium text-gray-700 truncate">
                                  {team.name}
                                </span>
                              </div>
                                ))
                              ) : allTeams.length > 0 ? (
                                // Fallback: Show teams from all teams if groupTeams is empty
                                allTeams
                                  .filter((t) => t.group === group)
                                  .map((team, index) => (
                                    <div
                                      key={team.id}
                                      className="flex items-center px-3 py-2 rounded-lg bg-white border border-gray-200 text-xs shadow-sm hover:shadow-md transition-all duration-200 hover:border-[#2E55B9]"
                                    >
                                      <div className="w-6 h-6 rounded-full bg-[#2E55B9] text-white flex items-center justify-center text-xs font-bold mr-2">
                                        {index + 1}
                          </div>
                                      <span className="font-medium text-gray-700 truncate">
                                        {team.name}
                                      </span>
                        </div>
                                  ))
                              ) : (
                                <div className="col-span-full text-gray-500 text-xs italic text-center py-4">
                                  <div className="mb-2">
                                    No teams in this group.
                                  </div>
                                  <div className="text-xs">
                                    Please check form data and generate schedule.
                                  </div>
                                  <div className="text-xs mt-1">
                                    Teams: {formData.numberOfTeams}, Groups:{" "}
                                    {formData.numberOfGroups}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div> */}

                        {/* Matches in Group - Grouped by Date */}
                        <div className="space-y-3">
                          {(() => {
                            // Group matches by date
                            const matchesByDate = groupMatches.reduce(
                              (acc, match) => {
                                const dayOffset = getDateFromTime(
                                  match.startTime
                                );
                                const dateKey = dayOffset;
                                if (!acc[dateKey]) {
                                  acc[dateKey] = [];
                                }
                                acc[dateKey].push(match);
                                return acc;
                              },
                              {}
                            );

                            return Object.entries(matchesByDate)
                              .sort(([a], [b]) => parseInt(a) - parseInt(b))
                              .map(([dateKey, matches]) => {
                                const dayOffset = parseInt(dateKey);
                                const dateStr = formatDateOnly(dayOffset);

                                return (
                                  <div key={dateKey} className="space-y-1">
                                    {/* Date Header - Only show if there are multiple days or it's not day 0 */}
                                    {dateStr && (
                                      <div className="flex items-center space-x-2 mb-2">
                                        <div className="h-px bg-gray-300 flex-1"></div>
                                        <div className="px-3 py-1 bg-gray-100 rounded-full text-xs font-semibold text-gray-600">
                                          {dateStr}
                                        </div>
                                        <div className="h-px bg-gray-300 flex-1"></div>
                                      </div>
                                    )}

                                    {/* Matches for this date */}
                                    {matches.map((match, index) => (
                                      <motion.div
                                        key={match.id}
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.01 }}
                                        className={`p-2 rounded border ${getGroupBgColor(
                                          match.group
                                        )} hover:shadow-sm transition-shadow`}
                                      >
                                        <div className="flex items-center justify-between text-xs">
                                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                                            <div className="w-6 h-6 rounded bg-white border border-gray-200 flex items-center justify-center font-bold text-[#0C1B3A] text-xs">
                                              #{match.matchNumber}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center space-x-1">
                                                <span className="font-semibold text-gray-800 truncate">
                                                  {match.team1}
                                                </span>
                                                <span className="text-gray-400">
                                                  vs
                                                </span>
                                                <span className="font-semibold text-gray-800 truncate">
                                                  {match.team2}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2 text-gray-600">
                                            <div className="flex items-center bg-white px-2 py-1 rounded border border-gray-200">
                                              <FiMapPin className="mr-1 text-[#2E55B9] w-3 h-3" />
                                              <span>{match.court}</span>
                                            </div>
                                            <div className="flex items-center bg-white px-2 py-1 rounded border border-gray-200">
                                              <FiClock className="mr-1 text-[#2E55B9] w-3 h-3" />
                                              <span className="font-medium text-xs">
                                                {formatTimeOnly(
                                                  match.startTime
                                                )}{" "}
                                                -{" "}
                                                {formatTimeOnly(match.endTime)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    ))}
                                  </div>
                                );
                              });
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiCalendar className="text-2xl text-gray-400" />
                  </div>
                  <p className="text-gray-400">No schedule generated yet</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Configure your tournament and click "Generate Schedule"
                  </p>
                </div>
              )}
            </div>

            {/* Conflicts Display */}
            {scheduleConflicts.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mt-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">!</span>
                  </div>
                  <h3 className="text-lg font-bold text-red-800">
                    Schedule Conflicts Detected
                  </h3>
                </div>
                <div className="space-y-2">
                  {scheduleConflicts.map((conflict, index) => (
                    <div
                      key={index}
                      className="bg-white border border-red-200 rounded-lg p-3"
                    >
                      <div className="font-semibold text-red-700 mb-2">
                        {conflict.team} has overlapping matches:
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="font-medium">Match 1:</div>
                          <div>vs {conflict.match1.opponent}</div>
                          <div>Court: {conflict.match1.court}</div>
                          <div>
                            Time: {formatTimeOnly(conflict.match1.startTime)} -{" "}
                            {formatTimeOnly(conflict.match1.endTime)}
                          </div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="font-medium">Match 2:</div>
                          <div>vs {conflict.match2.opponent}</div>
                          <div>Court: {conflict.match2.court}</div>
                          <div>
                            Time: {formatTimeOnly(conflict.match2.startTime)} -{" "}
                            {formatTimeOnly(conflict.match2.endTime)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-sm text-red-600">
                  <strong>Note:</strong> These conflicts should not occur with
                  the new scheduling algorithm. If you see this message, please
                  report it as a bug.
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Column - Teams & Courts */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-1 space-y-4"
          >
            {/* Teams */}
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <FiUsers className="text-xl text-[#2E55B9] mr-2" />
                <h3 className="text-lg font-bold text-[#0C1B3A]">Teams</h3>
              </div>

              {teams.length > 0 ? (
                <div className="space-y-1 max-h-96 overflow-y-auto custom-scrollbar">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"
                    >
                      {editingTeam === team.id ? (
                        <input
                          type="text"
                          defaultValue={team.name}
                          autoFocus
                          onBlur={(e) =>
                            handleTeamNameChange(team.id, e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleTeamNameChange(team.id, e.target.value);
                            }
                          }}
                          className="flex-1 px-2 py-1 border-2 border-[#2E55B9] rounded focus:outline-none text-sm"
                        />
                      ) : (
                        <>
                          <span className="flex-1 font-medium text-gray-700 text-sm">
                            {team.name}
                          </span>
                          <button
                            onClick={() => setEditingTeam(team.id)}
                            className="text-gray-400 hover:text-[#2E55B9] transition-colors"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">
                  Configure teams to see them here
                </p>
              )}
            </div>

            {/* Courts */}
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <FiMapPin className="text-xl text-[#2E55B9] mr-2" />
                <h3 className="text-lg font-bold text-[#0C1B3A]">Courts</h3>
              </div>

              {courts.length > 0 ? (
                <div className="space-y-1 max-h-96 overflow-y-auto custom-scrollbar">
                  {courts.map((court) => (
                    <div
                      key={court.id}
                      className="flex items-center p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"
                    >
                      {editingCourt === court.id ? (
                        <input
                          type="text"
                          defaultValue={court.name}
                          autoFocus
                          onBlur={(e) =>
                            handleCourtNameChange(court.id, e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleCourtNameChange(court.id, e.target.value);
                            }
                          }}
                          className="flex-1 px-2 py-1 border-2 border-[#2E55B9] rounded focus:outline-none text-sm"
                        />
                      ) : (
                        <>
                          <span className="flex-1 font-medium text-gray-700 text-sm">
                            {court.name}
                          </span>
                          <button
                            onClick={() => setEditingCourt(court.id)}
                            className="text-gray-400 hover:text-[#2E55B9] transition-colors"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">
                  Configure courts to see them here
                </p>
              )}
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default TournamentCalculator;
